import { Discount, FreeGiftRule } from '@emico-hooks/cart-fragment'
import { i18n } from '@lingui/core'
import { Plural, Trans } from '@lingui/macro'
import classNames from 'classnames'
import { ReactNode } from 'react'

import { stripMaybes } from '@emico/utils'

import { DiscountTooltip } from './DiscountTooltip'
import getShippingEstimate from './getShippingEstimate'
import styles from './Totals.module.scss'
import { SegmentCodes, segmentCodeTitles } from './utils'
import { useGetCustomerCoupons } from '../../account-dashboard-page/AccountDashboardPageLayout/justRewards/Vouchers/customerVouchers.query'
import RemoveVoucherButton from '../../account-dashboard-page/AccountDashboardPageLayout/justRewards/Vouchers/RemoveVoucherButton'
import BinIcon from '../../core/BinIcon'
import { CustomerCoupon } from '../../graphql/schema.generated'
import Separator from '../../layout/Separator'
import Icon from '../../media/Icon'
import Loader from '../../presentation/Loader'
import Price from '../../presentation/Price'
import {
    QuoteDataShippingEstimateInterface,
    QuoteDataTotalSegmentExtensionInterface,
} from '../../types'
import { useCartItems, useCheckoutDelivery } from '../CartPage/useCartItems'

const SegmentGroup = ({ children }: { children?: ReactNode }) => (
    <div className={styles.group}>{children}</div>
)
const SegmentItem = ({ children }: { children?: ReactNode }) => (
    <div className={styles.segment}>{children}</div>
)

export function Totals({
    hasLinkedGiftCards,
}: {
    hasLinkedGiftCards?: boolean
}) {
    const {
        prices,
        discounts,
        totalItemsExclGifts,
        loading: loadingCart,
    } = useCartItems()

    const {
        defaultShippingMethod,
        extensionAttributes,
        loading: loadingCheckoutDelivery,
    } = useCheckoutDelivery(true)

    const { data: coupons, loading: loadingVouchers } = useGetCustomerCoupons()
    const activeCoupons = (coupons || []).filter((item) => item.isActive)

    const { freeGifts } = useCartItems()
    const activeFreeGifts = freeGifts?.filter((gift) =>
        gift?.items?.find((itm) => Boolean(itm.item)),
    )

    const hasShippingCosts =
        defaultShippingMethod?.priceInclTax?.value !== undefined

    const shippingEstimate = parseFloat(
        extensionAttributes?.shippingEstimateMin ?? '0',
    )
    const isLoading = loadingVouchers || loadingCart || loadingCheckoutDelivery

    return (
        <div className={styles.container}>
            {isLoading && (
                <div className={styles.loaderBig}>
                    <Loader className={styles.loader} />
                </div>
            )}
            <SegmentGroup>
                <SegmentItem>
                    <RenderSubTotal
                        numItems={totalItemsExclGifts ?? 0}
                        value={prices?.subtotalIncludingTax?.value ?? 0}
                    />
                </SegmentItem>

                <SegmentItem>
                    <RenderShipping
                        loading={loadingCheckoutDelivery}
                        value={shippingEstimate}
                    />
                </SegmentItem>
                <Separator />
            </SegmentGroup>

            <SegmentGroup>
                <RenderDiscount
                    discounts={discounts}
                    freeGifts={activeFreeGifts}
                    coupons={activeCoupons}
                />
            </SegmentGroup>
            <SegmentGroup>
                <SegmentItem>
                    <RenderGrandTotal
                        hasLinkedGiftCards={hasLinkedGiftCards}
                        value={prices?.grandTotal?.value ?? 0}
                        shippingCosts={hasShippingCosts ? 0 : shippingEstimate}
                    />
                </SegmentItem>
            </SegmentGroup>
        </div>
    )
}

const RenderSubTotal = ({
    numItems = 0,
    value,
}: {
    numItems: number
    value: number
}) => (
    <>
        <div className={styles.row}>
            {i18n._(segmentCodeTitles[SegmentCodes.SUBTOTAL])}{' '}
            <Plural
                id="cart.cartTotals.segment.subtotal"
                value={numItems ?? 0}
                zero="No items"
                one="(# item)"
                other="(# items)"
            />
        </div>
        <Price price={value} className={styles.price} />
    </>
)

const RenderShipping = ({
    estimate: shippingEstimate,
    value,
    loading,
}: {
    estimate?: QuoteDataShippingEstimateInterface
    value: number
    loading: boolean
}) => {
    let price
    if (shippingEstimate) {
        price = getShippingEstimate(shippingEstimate)
    } else if (value === null || value === 0) {
        price = (
            <span className={styles.freeOrDiscount}>
                <Trans id="cart.cartTotals.freeShippingLabel">Free</Trans>
            </span>
        )
    } else {
        price = <Price price={value} className={styles.price} />
    }

    return (
        <>
            {i18n._(segmentCodeTitles[SegmentCodes.SHIPPING])}
            {loading ? '' : price}
        </>
    )
}
const RenderGrandTotal = ({
    value,
    shippingCosts = 0,
    hasLinkedGiftCards,
}: {
    value: number
    shippingCosts: number
    hasLinkedGiftCards?: boolean
}) => (
    <>
        <span
            className={classNames(
                styles.grandTotal,
                hasLinkedGiftCards ? styles.light : '',
            )}
        >
            {i18n._(segmentCodeTitles[SegmentCodes.GRAND_TOTAL])}
        </span>
        <Price
            price={value + shippingCosts}
            className={classNames(
                styles.price,
                styles.grandTotal,
                hasLinkedGiftCards ? styles.light : '',
            )}
        />
    </>
)

const RenderDiscount = ({
    discounts,
    freeGifts,
    coupons,
}: {
    discounts?: Discount[]
    freeGifts?: FreeGiftRule[]
    value?: QuoteDataTotalSegmentExtensionInterface
    coupons?: CustomerCoupon[]
}) => {
    if (!coupons?.length && !freeGifts?.length && !discounts?.length) {
        return null
    }

    return (
        <>
            {discounts?.map((discount) => (
                <RenderVoucherWithCode
                    description={discount.label ?? ''}
                    key={discount.label}
                    code={discount.coupon?.code}
                    price={discount.amount?.value ?? 0}
                />
            ))}
            {freeGifts
                ?.flatMap((gift) => gift.products)
                .filter(stripMaybes)
                .map((product) => (
                    <SegmentItem key={product.id}>
                        {product.name}
                        <span className={styles.freeOrDiscount}>
                            <Trans id="cart.cartTotals.freeShippingLabel">
                                Free
                            </Trans>
                        </span>
                    </SegmentItem>
                ))}
            {freeGifts
                ?.filter((gift) => !gift.products || gift.products.length === 0)
                ?.map((gift) => (
                    <SegmentItem key={gift.name}>
                        {gift.name}
                        <span className={styles.freeOrDiscount}>
                            <Trans id="cart.cartTotals.freeShippingLabel">
                                Free
                            </Trans>
                        </span>
                    </SegmentItem>
                ))}
            <RenderCoupons coupons={coupons ?? []} />
            <Separator />
        </>
    )
}

const RenderVoucherWithCode = ({
    code,
    price,
    description,
}: {
    code: string
    description: string
    price: number
}) => (
    <SegmentItem>
        <div className={styles.row}>
            <RemoveVoucherButton
                variant="link"
                size="small"
                code={code}
                className={styles.binButton}
                hideLoader
            >
                {(loading: boolean) => (
                    <>
                        <Icon component={BinIcon} title="" />
                        {loading ? (
                            <Loader className={styles.miniLoader} />
                        ) : (
                            <span>
                                {description} <DiscountTooltip />
                            </span>
                        )}
                    </>
                )}
            </RemoveVoucherButton>
        </div>
        <Price price={-price} className={styles.price} />
    </SegmentItem>
)

const RenderCoupons = ({ coupons }: { coupons: CustomerCoupon[] }) => {
    if (!coupons || !coupons.length) {
        return null
    }

    return (
        <>
            {coupons.map((coupon) => (
                <RenderVoucherWithCode
                    key={coupon.code}
                    price={coupon.amount.value}
                    code={coupon.code}
                    description={coupon.description ?? ''}
                />
            ))}
        </>
    )
}
